<template>
  <div>
    <TreasurersChecksCriteria
      :baseCrudKey="baseCrudKey"
      @loadCRUDList="handleLoadCRUDList"
    />
    <CRUDList
      :canAdd="true"
      :canEdit="true"
      :canSave="true"
      :canDelete="false"
      :canDeleteInDialog="true"
      :showSelect="true"
      :selectedItems="selectedItems"
      :headers="headers"
      :baseCrudKey="baseCrudKey"
      :createNewItem="newItemDialog"
      :loadingData="gridLoading.flag"
      :loadingText="gridLoading.text"
      toolbarTitle="Treasurer's Checks"
      :enableFixedHeaders="true"
      tableDynamicHeight="400"
      @loadCRUDList="handleLoadCRUDList"
      @upsertItem="upsertRecord"
      @deleteItem="deleteRecord"
      @emitSelectedItem="onSelectedItems"
    >
      <template v-slot:[`editedItem`]="{}">
        <TreasurersChecksDialog :baseCrudKey="baseCrudKey" />
      </template>
      <template v-slot:[`canDeleteSlot`]="{}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              id="treasurersChecksDeleteIcon"
              class="mx-2"
              color="primary"
              fab icon small
              v-bind="attrs"
              v-on="on"
              :disabled="(selectedItems || []).length === 0"
              @click="deleteItems"
            >
              {{ icons.mdiDelete }}
            </v-icon>
          </template>
          <span>Delete</span>
        </v-tooltip>
        <v-divider class="mx-4" inset vertical></v-divider>
      </template>
      <template v-slot:[`additionalToolsAtRight`]="{}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              id="treasurersChecksPrintBtn"
              depressed
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              :disabled="(selectedItems || []).length === 0"
              @click="printReport"
            >
              <v-icon left>{{ icons.mdiPrinter }}</v-icon>
              Print Checks
            </v-btn>
          </template>
          <span>Print selected checks</span>
        </v-tooltip>
      </template>
      <template v-slot:[`additionalActions`]="{}">
        <UploadFile
          class="treasurers-checks-upload-file"
          bucketName="gl"
          program="gl"
          parentObjectType="treasurerCheck"
          :parentObjectId="editedItemId"
          :itemIsSaved="!!editedItemId"
        >
          <template v-slot:activator="{ activatorProps }">
            <v-btn
              color="primary"
              text
              @contextmenu.prevent="activatorProps.rightClick()"
              @click="activatorProps.leftClick()"
            >
              {{ activatorProps.caption }}
            </v-btn>
          </template>
        </UploadFile>
      </template>
    </CRUDList>
    <TreasurersChecksJEDialog
      :dialog="jeDialog"
      :check="savedCheck"
      @dialogVal="jeDialog = $event"
    />
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import { mdiPrinter, mdiDelete } from '@mdi/js';
import CRUDList from '../../common/base/crud/CRUDList.vue';
import UploadFile from '../../common/UploadFile.vue';
import TreasurersChecksCriteria from './TreasurersChecksCriteria.vue';
import TreasurersChecksDialog from './TreasurersChecksDialog.vue';
import TreasurersChecksJEDialog from './TreasurersChecksJEDialog.vue';
import {
  amountToNumber,
  defaultGridLoading,
  manageGridLoading,
} from '../../../util/shared/vue-global';
import { toIsoDate } from '../../../util/shared/tmc-global';
import { clients } from '../../../util/clients';
import { generateQueryString } from '../../common/base/report/report-util';

const { backendRest } = clients.direct;

const getHeaders = () => [
  { text: 'Bank', value: 'bankName' },
  { text: 'Date', value: 'date', formatter: 'shortDate' },
  { text: 'Check #', value: 'checkNo' },
  { text: 'To Whom', value: 'payee' },
  { text: 'Amount', value: 'amount', formatter: 'amount' },
  { text: 'Paid Date', value: 'paidDate', formatter: 'shortDate' },
];

export default {
  name: 'TreasurersChecksList',
  components: {
    CRUDList,
    UploadFile,
    TreasurersChecksCriteria,
    TreasurersChecksDialog,
    TreasurersChecksJEDialog,
  },
  data: () => ({
    baseCrudKey: 'treasurersChecks',
    headers: [],
    gridLoading: defaultGridLoading(),
    icons: { mdiPrinter, mdiDelete },
    selectedItems: [],
    jeDialog: false,
    savedCheck: {},
  }),
  created() {
    this.headers = getHeaders();
    this.setCriteria([this.baseCrudKey, {}]);
    this.handleLoadCRUDList();
    this.loadConfig({ requiredScope: 'glConfig' });
  },
  computed: {
    ...mapGetters('base/crud', ['criteria', 'items', 'item']),
    ...mapState({
      glConfig: (state) => state.SystemConfig.glSettingItem || {},
    }),
    criteriaState() {
      return this.criteria(this.baseCrudKey) || {};
    },
    editedItemId() {
      return (this.item(this.baseCrudKey) || {})._id || '';
    },
  },
  methods: {
    ...mapMutations('base/crud', ['setCriteria']),
    ...mapActions(['reAuth']),
    ...mapActions('SystemConfig', ['loadConfig']),
    ...mapActions('glDaily/treasurersChecks', [
      'loadTreasurersChecks',
      'upsertTreasurerCheck',
      'deleteTreasurerCheck',
    ]),
    onSelectedItems(items) {
      this.selectedItems = items || [];
    },
    async printReport() {
      const selectedIds = (this.selectedItems || []).map((i) => i._id).filter(Boolean);
      if (selectedIds.length === 0) {
        this.$store.dispatch('flashWarn', 'Select at least one check to print.');
        return;
      }
      const { jwt } = await this.reAuth();
      const filters = JSON.stringify({ _ids: selectedIds });
      const params = [
        `token=${jwt}`,
        `filters=${encodeURIComponent(filters)}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/gl/treasurers-checks-report${queryString}`;
      window.open(url, '_blank');
    },
    handleLoadCRUDList() {
      const { baseCrudKey } = this;
      manageGridLoading(this, true, 'Loading...');
      this.loadTreasurersChecks({
        criteria: this.criteriaState,
        baseCrudKey,
      }).finally(() => {
        manageGridLoading(this, false);
        this.$nextTick(() => {
          this.selectedItems = [];
        });
      });
    },
    async deleteItems() {
      const items = this.selectedItems || [];
      if (items.length === 0) return;
      if (!window.confirm(`Delete ${items.length} treasurer's check record(s)?\n\nNote: any journal entries already created for these checks will NOT be deleted.`)) return;
      const { baseCrudKey } = this;
      for (let i = 0; i < items.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await this.deleteTreasurerCheck({ item: items[i], baseCrudKey });
      }
      this.selectedItems = [];
      this.handleLoadCRUDList();
    },
    async upsertRecord(item) {
      const { baseCrudKey } = this;
      const isNewCheck = !item._id;
      const updated = { ...item };
      updated.amount = amountToNumber(updated.amount);
      updated.date = updated.date ? toIsoDate(updated.date) : null;
      updated.paidDate = updated.paidDate ? toIsoDate(updated.paidDate) : null;
      const saved = await this.upsertTreasurerCheck({ item: updated, baseCrudKey });
      // Only prompt to create journal entries for brand-new checks.
      if (isNewCheck && saved && saved._id) {
        this.savedCheck = {
          bankId: saved.bankId || updated.bankId,
          amount: saved.amount != null ? saved.amount : updated.amount,
          date: saved.date || updated.date,
          checkNo: saved.checkNo || updated.checkNo,
          payee: saved.payee || updated.payee,
          forWhat: saved.forWhat || updated.forWhat,
        };
        this.jeDialog = true;
      }
    },
    async deleteRecord(item) {
      const { baseCrudKey } = this;
      await this.deleteTreasurerCheck({ item, baseCrudKey });
    },
    newItemDialog() {
      const bankId = this.criteriaState.bankId
        || this.glConfig.treasurerCheckDefaultBank
        || null;
      const bankList = this.items('treasurersChecksBankList') || [];
      const selectedBank = bankList.find((b) => b._id === bankId) || {};
      const currentItems = this.items(this.baseCrudKey) || [];
      const bankItems = bankId
        ? currentItems.filter((i) => i.bankId === bankId)
        : currentItems;
      const maxCheckNo = bankItems.reduce((max, i) => {
        const n = Number(i.checkNo);
        return n > max ? n : max;
      }, 0);
      return {
        bankId,
        bankName: selectedBank.bankName || '',
        date: null,
        checkNo: maxCheckNo ? maxCheckNo + 1 : 1,
        payee: '',
        amount: 0,
        paidDate: null,
      };
    },
  },
};
</script>
